import { Avatar } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, stringToColor } from "@/lib/utils";
import { type Conversation, type ConversationDetail, type SmsCredits } from "@/types/crm/sms";

interface ConversationsSidebarProps {
    conversations: Conversation[];
    selectedConversation: ConversationDetail | null;
    onSelect: (conversation: Conversation) => void;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoading?: boolean;
    smsCredits?: SmsCredits | null;
    isLoadingSmsCredits?: boolean;
    isLoadingMore?: boolean;
}

function getInitials(name: string): string {
    return name
        .split(" ")
        .map(part => part.trim()[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function formatDate(date: string | null): string {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";

    const now = new Date();

    // Helper to get local start-of-day
    const startOfDay = (dt: Date) => {
        const s = new Date(dt);
        s.setHours(0, 0, 0, 0);
        return s;
    };

    const todayStart = startOfDay(now);
    const targetStart = startOfDay(d);

    const msInDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((todayStart.getTime() - targetStart.getTime()) / msInDay);

    if (diffDays === 0) {
        // Today → HH:MM (24h)
        return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    }

    if (diffDays === 1) {
        // Yesterday
        return "Yesterday";
    }

    if (diffDays < 7 && diffDays > 1) {
        // Within last 7 days → weekday name
        return d.toLocaleDateString("en-US", { weekday: "long" });
    }

    // Older → dd/mm/yy
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
}

function getDateGroup(date: string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Previously";

    const now = new Date();

    // Helper to get local start-of-day
    const startOfDay = (dt: Date) => {
        const s = new Date(dt);
        s.setHours(0, 0, 0, 0);
        return s;
    };

    const todayStart = startOfDay(now);
    const targetStart = startOfDay(d);

    const msInDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((todayStart.getTime() - targetStart.getTime()) / msInDay);

    if (diffDays === 0) {
        return "Today";
    }

    if (diffDays === 1) {
        return "Yesterday";
    }

    if (diffDays < 7 && diffDays > 1) {
        // Within last 7 days → weekday name in French
        const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return weekdays[d.getDay()];
    }

    return "Previously";
}

function groupConversationsByDate(conversations: Conversation[]): { [key: string]: Conversation[] } {
    const groups: { [key: string]: Conversation[] } = {};

    conversations.forEach(conversation => {
        const groupKey = getDateGroup(conversation.last_activity_at || conversation.updated_at);
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(conversation);
    });

    return groups;
}

export function ConversationsSidebar({
    conversations,
    selectedConversation,
    onSelect,
    onLoadMore,
    hasMore = false,
    isLoading = false,
    smsCredits,
    isLoadingSmsCredits,
    isLoadingMore
}: ConversationsSidebarProps) {
    const handleScroll: React.UIEventHandler<HTMLDivElement> = e => {
        if (!onLoadMore || !hasMore || isLoading || isLoadingMore) return;
        const el = e.currentTarget;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining < 80) {
            onLoadMore();
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full w-64 flex-shrink-0 flex-col justify-between rounded-l-md border-r bg-gray-50">
                <ScrollArea className="flex h-full w-full overflow-x-hidden" onViewportScroll={handleScroll}>
                    <div className="flex h-full w-64 flex-shrink-0 flex-col justify-between rounded-l-md border-r bg-gray-50">
                        <div className="flex items-center justify-center py-4">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                                <span>Loading conversations...</span>
                            </div>
                        </div>
                    </div>
                </ScrollArea>
            </div>
        );
    }

    const groupedConversations = groupConversationsByDate(conversations);

    // Define the order of date groups
    const dateGroupOrder = ["Today", "Yesterday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", "Previously"];

    return (
        <div className="flex h-full w-64 flex-shrink-0 flex-col justify-between rounded-l-md border-r bg-gray-50">
            <ScrollArea className="flex h-full w-full overflow-x-hidden" onViewportScroll={handleScroll}>
                <div className="flex w-full flex-col overflow-x-hidden p-2">
                    {dateGroupOrder.map(groupKey => {
                        const groupConversations = groupedConversations[groupKey];
                        if (!groupConversations || groupConversations.length === 0) return null;

                        return (
                            <div key={groupKey}>
                                {/* Date separator */}
                                <div className="mb-2 mt-3 px-2">
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{groupKey}</div>
                                </div>

                                {/* Conversations in this group */}
                                {groupConversations.map(conversation => {
                                    const isActive = conversation.id === selectedConversation?.id;
                                    const accountName = conversation.sf_contact?.name || conversation.sf_account?.name || conversation.sf_lead?.name || `Conversation #${conversation.id}`;
                                    const lastMessage = conversation.last_task?.description || "";
                                    const lastDatetime = conversation.last_activity_at;
                                    const hasUnread = conversation.seen === false;
                                    return (
                                        <div
                                            key={conversation.id}
                                            className={cn("group mb-1 flex w-full cursor-pointer items-center gap-2 rounded-md p-2 hover:bg-gray-100", isActive && "bg-gray-100")}
                                            onClick={() => onSelect(conversation)}
                                        >
                                            <Avatar
                                                className="flex h-8 w-8 items-center justify-center text-xs font-medium text-white"
                                                style={{ backgroundColor: stringToColor(accountName || conversation.mobile_phone || "") }}
                                            >
                                                {getInitials(accountName || conversation.mobile_phone || "")}
                                            </Avatar>
                                            <div className="w-0 min-w-0 flex-1 overflow-hidden">
                                                <div className="flex items-center justify-between">
                                                    <div className="max-w-[10rem] truncate text-sm font-medium">{accountName || conversation.mobile_phone}</div>
                                                    <div className="ml-2 flex items-center gap-2">
                                                        {hasUnread && <span className="flex h-2.5 w-2.5 items-center justify-center rounded-full bg-azure-blue"></span>}
                                                        <div className="shrink-0 text-[10px] text-gray-500">{formatDate(lastDatetime)}</div>
                                                    </div>
                                                </div>
                                                <div className="max-w-[10rem] truncate text-xs text-gray-500">{lastMessage}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </ScrollArea>
            <div className="flex items-center justify-center gap-2 px-2 py-2">
                <span className="text-center text-[10px] text-gray-500">
                    {isLoadingSmsCredits
                        ? "Loading SMS credits..."
                        : smsCredits
                          ? `${smsCredits.sms_credits_limit - smsCredits.remaining_sms} of ${smsCredits.sms_credits_limit} daily SMS used`
                          : "No SMS credits found"}
                </span>
            </div>
        </div>
    );
}
